import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormBuilder, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import {
  AppointmentModel,
  AuthorizationRulesService,
  NecessityService,
  NecessityStatus,
  NecessityType,
  PatientInsurance,
  PermissionsService,
  ProductModel,
  REFERRAL_STATUS,
  SelectOption,
} from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  finalize,
  map,
  startWith,
  Subject,
  takeUntil,
} from 'rxjs'

export interface ConfirmNecessityDialogData {
  type: NecessityType
  appointment: AppointmentModel
  insurance: PatientInsurance
  status?: NecessityStatus
  authorizationRequired?: boolean
  availableCodes?: string[]
  availableProducts?: ProductModel[]
}

@Component({
  selector: 'mmx-confirm-necessity-dialog',
  templateUrl: './confirm-necessity-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ConfirmNecessityDialogComponent implements OnInit, OnDestroy {
  public insurances: PatientInsurance[] = []
  public availableCodes: string[] = []
  public availableProducts: ProductModel[] = []

  public formGroup = this.fb.group({
    status: this.fb.control<NecessityStatus>(null, Validators.required),
    insurance: this.fb.control<PatientInsurance>(null, Validators.required),
    codes: this.fb.control<string>({ value: '', disabled: true }),
    selectedCodes: this.fb.control<string[]>([]),
    selectedProducts: this.fb.control<ProductModel[]>([]),
  })

  public typesMap: Record<NecessityType, SelectOption<string>[]> = {
    [NecessityType.AUTHORIZATION]: [
      // {
      //   viewValue: 'icons.authorizations.mark-required',
      //   value: AUTHORIZATION_STATUS.NOT_INITIATED,
      // },
      // {
      //   viewValue: 'icons.authorizations.mark-not-required',
      //   value: AUTHORIZATION_STATUS.NOT_REQUIRED,
      // },
    ],
    [NecessityType.REFERRAL]: [
      {
        viewValue: 'icons.referrals.mark-required',
        value: REFERRAL_STATUS.REQUIRED,
      },
      {
        viewValue: 'icons.referrals.mark-not-required',
        value: REFERRAL_STATUS.NOT_REQUIRED,
      },
    ],
  }

  public loading$ = new BehaviorSubject<'necessity' | 'rule' | null>(null)

  public saveDisabled$ = combineLatest([
    this.loading$,
    this.formGroup.statusChanges.pipe(startWith(this.formGroup.status)),
  ]).pipe(
    map(
      ([loading]) =>
        loading === 'necessity' ||
        this.formGroup.invalid ||
        !this.haveFieldsChanged(),
    ),
  )

  public get statuses() {
    return this.typesMap[this.data.type]
  }

  public rules$ = this.formGroup.get('insurance').valueChanges.pipe(
    startWith(this.formGroup.get('insurance').value),
    combineLatestWith(this.authorizationRulesService.collectionSuccess$),
    map(([, rules]) => {
      // const insurance = this.formGroup.get('insurance').value

      return rules.filter(() => {
        // @TODO: make this work with payer groups and codes only
        return true
        // if (!!rule.carrierLookupIds && !rule.carrierLookupIds.includes(insurance.carrierLookupId)) {
        //   return false
        // }

        // return (
        //   (rule.products || []).filter((p) => productIds.includes(p.id))
        //     .length > 0
        // )
      })
    }),
  )

  public saveRuleDisabled$ = combineLatest([
    this.rules$,
    this.loading$,
    this.formGroup.statusChanges.pipe(startWith(this.formGroup.status)),
  ]).pipe(
    map(
      ([rules, loading]) =>
        rules.length > 0 || loading === 'rule' || this.formGroup.invalid,
    ),
  )

  public isSaveRuleAvailable$ = combineLatest([
    this.rules$,
    this.loading$,
    this.permissionsService.hasPermission('auth.manager'),
  ]).pipe(
    map(
      ([rules, loading, hasPermission]) =>
        rules.length === 0 && loading !== 'rule' && hasPermission,
    ),
  )

  private onDestroy$ = new Subject<void>()

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ConfirmNecessityDialogData,
    private fb: FormBuilder,
    private authorizationRulesService: AuthorizationRulesService,
    private necessityService: NecessityService,
    private dialogRef: MatDialogRef<ConfirmNecessityDialogComponent>,
    private permissionsService: PermissionsService,
    private translateService: TranslateService,
    private snackbar: MatSnackBar,
  ) {}

  public ngOnInit() {
    this.insurances = this.data.appointment.insurances || []
    this.availableCodes = this.data.availableCodes || []
    this.availableProducts = this.data.availableProducts || []

    if (this.data.insurance) {
      const selected = this.insurances.find(
        (i) => i.id === this.data.insurance.id,
      )
      this.formGroup.patchValue({ insurance: selected || this.data.insurance })
    }

    if (this.data.authorizationRequired != null && this.data.type === NecessityType.AUTHORIZATION) {
      this.formGroup
        .get('status')
        // .patchValue(
        //   this.data.authorizationRequired
        //     ? AUTHORIZATION_STATUS.NOT_INITIATED
        //     : AUTHORIZATION_STATUS.NOT_REQUIRED,
        // )
    } else if (this.data.status) {
      this.formGroup.get('status').patchValue(this.data.status)
    }

    if (!this.availableCodes.length && !this.availableProducts.length) {
      this.formGroup.get('codes').patchValue(
        this.data.appointment.products
          .map((p) => p.getCPTCodes())
          .flat()
          .join(', '),
      )
    } else if (this.availableCodes.length) {
      this.formGroup.get('selectedCodes').setValue(this.availableCodes)
    } else if (this.availableProducts.length) {
      this.formGroup.get('selectedProducts').setValue(this.availableProducts)
    }

    if (this.permissionsService.instant('auth.manager')) {
      this.authorizationRulesService.collectionParams.next({})
    }
  }

  public ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public onSave() {
    const { appointment, type } = this.data
    const { insurance, status } = this.formGroup.value

    if (this.formGroup.invalid || !this.haveFieldsChanged()) {
      return
    }

    this.loading$.next('necessity')
    this.necessityService
      .save({
        appointmentId: appointment.id,
        type,
        status,
        insuranceId: insurance.id,
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => this.loading$.next(null)),
      )
      .subscribe(() => {
        this.dialogRef.close(true)
      })
  }

  public onSaveRule() {
    const { type } = this.data
    const { insurance, status } = this.formGroup.value

    if (this.formGroup.invalid) {
      return
    }

    this.loading$.next('rule')

    this.authorizationRulesService.updateResponse$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          this.loading$.next(null)
          this.authorizationRulesService.collectionParams.next({})
          this.openSnackBar(
            this.translateService.instant('dialog.necessity.rule-saved'),
          )
        },
        error: () => {
          this.loading$.next(null)
          this.openSnackBar(
            this.translateService.instant('dialog.necessity.rule-error'),
          )
        },
      })

    const selectedCodes: string[] = this.formGroup.value.selectedCodes || []
    const selectedProducts: ProductModel[] = this.formGroup.value.selectedProducts || []

    this.authorizationRulesService.updateParams.next({
      clinicCarrierId: insurance.clinicCarrierId,
      carrierLookupIds: [insurance.carrierLookupId],
      codes: this.availableCodes.length ? selectedCodes : undefined,
      products: this.availableProducts.length ? selectedProducts.map((p) => p.id) : undefined,
      referralRequired:
        type === NecessityType.REFERRAL
          ? status === REFERRAL_STATUS.REQUIRED
          : undefined,
      authorizationRequired: true,
    })
  }

  private haveFieldsChanged() {
    const { insurance, status } = this.formGroup.value

    return (
      status !== this.data.status || insurance.id !== this.data.insurance.id
    )
  }

  private openSnackBar(message: string) {
    this.snackbar.open(message, null, { duration: 5000 })
  }
}
